import React from 'react';
import Select, { MultiValue, StylesConfig } from 'react-select';

interface BasketItem {
  id: number | string;
  label: string;
}

interface BasketSelectProps {
  label: string;
  availableItems: BasketItem[];
  selectedIds: (number | string)[];
  onChange: (selectedIds: (number | string)[]) => void;
  required?: boolean;
  getItemLabel?: (item: BasketItem) => string;
  searchPlaceholder?: string;
  placeholder?: string;
}

interface SelectOption {
  value: number | string;
  label: string;
}

const BasketSelect: React.FC<BasketSelectProps> = ({
  label,
  availableItems,
  selectedIds,
  onChange,
  required = false,
  getItemLabel = (item) => item.label,
  searchPlaceholder = 'Search...',
  placeholder = 'Select items...'
}) => {
  // Convert items to react-select format
  const options: SelectOption[] = availableItems.map(item => ({
    value: item.id,
    label: getItemLabel(item)
  }));

  // Get selected options
  const selectedOptions = options.filter(option => selectedIds.includes(option.value));

  const handleChange = (newValue: MultiValue<SelectOption>) => {
    const newSelectedIds = newValue.map(option => option.value);
    onChange(newSelectedIds);
  };

  // Custom styles for react-select
  const customStyles: StylesConfig<SelectOption, true> = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.5)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
      },
      minHeight: '38px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#dbeafe',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1e40af',
      fontWeight: '500',
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1e40af',
      borderRadius: '0 6px 6px 0',
      '&:hover': {
        backgroundColor: '#93c5fd',
        color: '#1e3a8a',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af',
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
    }),
  };

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <Select<SelectOption, true>
        isMulti
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        styles={customStyles}
        isSearchable
        isClearable={false}
        className="react-select-container"
        classNamePrefix="react-select"
        noOptionsMessage={({ inputValue }) => 
          inputValue ? `No items found for "${inputValue}"` : 'No items available'
        }
      />
    </div>
  );
};

export default BasketSelect;
